import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFlashToast } from '../../../utils/flashToast';
import * as extraditionMlaService from '../../../services/extraditionMlaCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CriminalWizardDraft } from '../../../types/criminalCaseRequest';
import { useExtraditionMlaLookups } from './useExtraditionMlaLookups';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { calcAgeFromDob } from '../../../utils/caseFieldValidation';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import { apiOrLookupLabel, deliveryMethodDisplay } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
} from '../criminal/CriminalReviewUi';
import CommonCoverNoteModal from '../../../components/wizard/CommonCoverNoteModal';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaWizardPath } from './ExtraditionMlaWizardShell';
import { extraditionMlaDraftToCoverNoteFallback } from './extraditionMlaCoverNoteFallback';
import { requiresCustodialAuthority } from './extraditionMlaHelpers';
import { WizardReviewBreadcrumb, WizardReviewConfirm, WizardReviewSubtitle } from '../WizardReviewChrome';
import {
  wizardField,
  wizardReviewStepLabel,
  wizardStepTitle,
  wizardYesNo,
} from '../wizardI18nHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

export default function ExtraditionMlaReviewPage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useExtraditionMlaLookups('review', draft.provinceId);
  const accusedAge = useMemo(() => calcAgeFromDob(draft.accusedDob), [draft.accusedDob]);
  const selectedCustodyStatus = useMemo(
    () => lookups.custodyStatuses.find((s) => s.id === draft.custodyStatusId),
    [draft.custodyStatusId, lookups.custodyStatuses],
  );
  const showCustodialAuthority = requiresCustodialAuthority(
    draft.custodyStatusName,
    selectedCustodyStatus?.code,
  );

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods),
    [draft, lookups.deliveryMethods],
  );

  const coverNoteFallback = useMemo(
    () =>
      extraditionMlaDraftToCoverNoteFallback(draft, {
        fileRef: coverNoteFileRef || draft.referenceNo,
        submittedDivisionName: draft.submittedDivisionName || profile?.divisionUnit,
      }),
    [coverNoteFileRef, draft, profile?.divisionUnit],
  );

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'emla-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await extraditionMlaService.saveExtraditionMlaRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? t('wizard.nav.submitFailed'), variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: t('wizard.nav.submitSuccess'), variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = extraditionMlaWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4 | 5 | 6) => extraditionMlaWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <CommonCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        coverNoteFallback={coverNoteFallback}
        showSuccessBanner
        onClose={closeCoverNote}
      />

      <WizardReviewBreadcrumb reviewCrumbTo={reviewCrumbTo} />
      <WizardReviewSubtitle />

      <ReviewSection
        step={wizardReviewStepLabel(t, 1)}
        title={t('wizard.extraditionMla.reviewSteps.1categorization')}
        editTo={editTo(1)}
      >
        <ReviewGrid>
          <ReviewField label={wizardField(t, 'fileCategory')} value={draft.fileCategory} />
          <ReviewField label={wizardField(t, 'subFileCategory')} value={draft.subFileCategoryName} />
        </ReviewGrid>
        <ReviewChips items={draft.applicableAreas} />
      </ReviewSection>

      <ReviewSection
        step={wizardReviewStepLabel(t, 1)}
        title={t('wizard.extraditionMla.reviewSteps.1intake')}
        editTo={editTo(1)}
      >
        <ReviewGrid>
          <ReviewField label={wizardField(t, 'description')} value={draft.description} fullWidth />
          <ReviewField label={wizardField(t, 'caseSource')} value={draft.caseSource} fullWidth />
          <ReviewField
            label={wizardField(t, 'countryInvolved')}
            value={apiOrLookupLabel(draft.countryInvolvedName, draft.countryInvolvedId, lookups.countries)}
          />
          <ReviewField label={wizardField(t, 'directionOfRequest')} value={draft.directionOfRequest} />
          <ReviewField label={wizardField(t, 'province')} value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)} />
          <ReviewField label={wizardField(t, 'district')} value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)} />
          <ReviewField label={wizardField(t, 'yourReferenceNumber')} value={draft.yourReferenceNumber} />
          <ReviewField label={wizardField(t, 'otherExternalReferences')} value={draft.otherExternalReferences} />
          <ReviewField label={wizardField(t, 'caseNumber')} value={draft.caseNumber} />
          <ReviewField label={wizardField(t, 'connectedMattersShort')} value={wizardYesNo(t, draft.hasConnectedMatters)} />
          {draft.hasConnectedMatters === 'yes' ? (
            <>
              <ReviewField label={wizardField(t, 'connectedCaseNumber')} value={draft.connectedCaseNumber} />
              <ReviewField
                label={wizardField(t, 'connectedAgdFileRef')}
                value={draft.connectedAgdFileRefAvailable ? draft.connectedAgdFileRef : '—'}
              />
            </>
          ) : null}
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 2)} title={wizardStepTitle(t, 'extraditionMla', 2)} editTo={editTo(2)}>
        <ReviewGrid>
          <ReviewField label={wizardField(t, 'accusedName')} value={draft.accusedName} />
          <ReviewField
            label={wizardField(t, 'nationality')}
            value={apiOrLookupLabel(
              draft.nationalityName,
              draft.nationalityId,
              lookups.nationalities.length ? lookups.nationalities : lookups.countries,
            )}
          />
          <ReviewField
            label={wizardField(t, 'countryOfOrigin')}
            value={apiOrLookupLabel(draft.countryOfOriginName, draft.countryOfOriginId, lookups.countries)}
          />
          <ReviewField label={wizardField(t, 'dateOfBirth')} value={formatDisplayDate(draft.accusedDob)} />
          <ReviewField label={wizardField(t, 'age')} value={typeof accusedAge === 'number' ? String(accusedAge) : '—'} />
          <ReviewField label={wizardField(t, 'gender')} value={apiOrLookupLabel(draft.accusedGenderName, draft.accusedGenderId, lookups.genders)} />
          <ReviewField label={wizardField(t, 'passportIdNumber')} value={draft.passportOrIdNumber} />
          <ReviewField
            label={wizardField(t, 'currentCustodyStatus')}
            value={apiOrLookupLabel(draft.custodyStatusName, draft.custodyStatusId, lookups.custodyStatuses)}
          />
          {showCustodialAuthority ? (
            <ReviewField label={wizardField(t, 'custodialAuthority')} value={draft.custodialAuthority} />
          ) : null}
          <ReviewField label={wizardField(t, 'lastKnownLocation')} value={draft.lastKnownLocation} fullWidth />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection
        step={wizardReviewStepLabel(t, 3)}
        title={t('wizard.extraditionMla.reviewSteps.3')}
        editTo={editTo(3)}
      >
        <ReviewGrid>
          <ReviewField label={wizardField(t, 'arrestWarrantAvailable')} value={wizardYesNo(t, draft.arrestWarrantAvailable)} />
          {draft.arrestWarrantAvailable === 'yes' ? (
            <ReviewField label={wizardField(t, 'warrantReference')} value={draft.warrantReference} />
          ) : null}
          <ReviewField label={wizardField(t, 'applicableTreatyArrangement')} value={draft.applicableTreatyArrangement} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 4)} title={wizardStepTitle(t, 'extraditionMla', 4)} editTo={editTo(4)}>
        <ReviewGrid>
          <ReviewField
            label={wizardField(t, 'urgencyLevel')}
            value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
          />
          <ReviewField label={wizardField(t, 'deadline')} value={formatDisplayDate(draft.deadline)} />
          <ReviewField label={wizardField(t, 'urgencyJustification')} value={draft.urgencyJustification} fullWidth />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 5)} title={wizardStepTitle(t, 'extraditionMla', 5)} editTo={editTo(5)}>
        <ReviewGrid>
          <ReviewField
            label={wizardField(t, 'submittedDivisionUnit')}
            value={draft.submittedDivisionName || profile?.divisionUnit || profile?.externalAgencyName || '—'}
          />
          <ReviewField
            label={wizardField(t, 'submittedBy')}
            value={draft.submittedByDisplayName || profile?.displayName || '—'}
          />
          <ReviewField
            label={wizardField(t, 'designation')}
            value={draft.submitterDesignation || profile?.contactDesignation || '—'}
          />
          <ReviewField label={wizardField(t, 'emailAddress')} value={draft.submitterEmail || profile?.email || '—'} />
          <ReviewField label={wizardField(t, 'contactNumber')} value={draft.submitterContactNo || profile?.contactNumber || '—'} />
          <ReviewField label={wizardField(t, 'deliveryMethod')} value={deliveryLabel} fullWidth />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 6)} title={wizardStepTitle(t, 'extraditionMla', 6)} editTo={editTo(6)}>
        <CriminalReviewDocumentsTable documents={draft.documents} />
      </ReviewSection>

      <WizardReviewConfirm
        id="emla-confirm"
        checked={draft.confirmCorrect}
        error={errors.confirmCorrect}
        onChange={(checked) => {
          setDraft((d) => ({ ...d, confirmCorrect: checked }));
          setErrors((prev) => ({ ...prev, confirmCorrect: '' }));
        }}
      />

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={6}
        onBack={() => nav(extraditionMlaWizardPath(6, draft.draftRequestId))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
